import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const [glitchText, setGlitchText] = useState('404');
  const [terminalLines, setTerminalLines] = useState<string[]>([]);

  useEffect(() => {
    const errorMessages = [
      '> ERROR: Neural pathway not found',
      '> SCANNING: Alternative routes...',
      '> STATUS: Connection terminated',
      '> SUGGESTION: Return to neural core',
      '> FAILSAFE: Initiating redirect protocol'
    ];
    // Glitch effect for 404 text
    const glitchInterval = setInterval(() => {
      const glitches = ['404', '4Ø4', '4０4', '４０４', 'ERR', '404'];
      setGlitchText(glitches[Math.floor(Math.random() * glitches.length)]);
      
      setTimeout(() => setGlitchText('404'), 100);
    }, 2000);

    // Terminal line animation
    let lineIndex = 0;
    const lineInterval = setInterval(() => {
      if (lineIndex < errorMessages.length) {
        setTerminalLines(prev => [...prev, errorMessages[lineIndex]]);
        lineIndex++;
      } else {
        clearInterval(lineInterval);
      }
    }, 800);

    return () => {
      clearInterval(glitchInterval);
      clearInterval(lineInterval);
    };
  }, []);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 z-[1] opacity-20">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/30 to-black"></div>
      </div>

      {/* Static and scan lines */}
      <div className="fixed inset-0 z-[2] pointer-events-none">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 0, 0, 0.03) 2px, rgba(255, 0, 0, 0.03) 4px)',
            backgroundSize: '100% 4px'
          }}
        ></div>
        {/* Error corner indicators */}
        <div className="absolute top-4 left-4 w-12 h-12 border-l-4 border-t-4 border-red-400/60"></div>
        <div className="absolute top-4 right-4 w-12 h-12 border-r-4 border-t-4 border-red-400/60"></div>
        <div className="absolute bottom-4 left-4 w-12 h-12 border-l-4 border-b-4 border-red-400/60"></div>
        <div className="absolute bottom-4 right-4 w-12 h-12 border-r-4 border-b-4 border-red-400/60"></div>
      </div>

      {/* Floating error particles */}
      <div className="fixed inset-0 pointer-events-none z-[3]">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-red-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4">
        {/* Main Error Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          {/* Giant 404 with glitch effect */}
          <motion.div
            className="text-8xl sm:text-9xl md:text-[12rem] font-bold text-red-400 mb-8 relative"
            style={{ fontFamily: 'monospace' }}
          >
            <motion.span
              key={glitchText}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative cyber-glow-strong"
            >
              {glitchText}
            </motion.span>
            
            {/* Glitch overlay effects */}
            <motion.div
              className="absolute inset-0 text-cyan-400 opacity-30"
              animate={{
                x: [0, 2, -2, 1, 0],
                y: [0, -1, 2, -1, 0]
              }}
              transition={{
                duration: 0.2,
                repeat: Infinity,
                repeatDelay: 1.8
              }}
            >
              {glitchText}
            </motion.div>
          </motion.div>

          {/* Error Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-2xl sm:text-4xl font-bold text-white mb-4"
          >
            NEURAL PATHWAY NOT FOUND
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-gray-400 text-lg sm:text-xl max-w-2xl"
          >
            The requested neural pathway has been disconnected or does not exist in the current reality matrix.
          </motion.p>
        </motion.div>

        {/* Terminal Error Log */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="w-full max-w-4xl"
        >
          {/* Terminal Header */}
          <div className="bg-black/80 border border-red-400/30 rounded-t-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full opacity-30"></div>
              <div className="ml-4 text-red-400 font-mono text-sm">
                bash@neural-core:~$ cat /var/log/system_error.log
              </div>
            </div>
          </div>

          {/* Terminal Content */}
          <div className="bg-black/90 border-l border-r border-b border-red-400/30 rounded-b-lg p-6 font-mono text-sm">
            <div className="space-y-2">
              {terminalLines.map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="text-red-400"
                >
                  {line}
                </motion.div>
              ))}
              
              {terminalLines.length === 5 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="pt-4 border-t border-red-400/30 mt-4"
                >
                  <div className="text-cyan-400 mb-2">
                    bash@neural-core:~$ echo "Available options:"
                  </div>
                  <div className="text-gray-400 space-y-1">
                    <div>• Return to neural core [RECOMMENDED]</div>
                    <div>• Navigate back to previous location</div>
                    <div>• Initialize system diagnostics</div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
          className="flex flex-col sm:flex-row items-center gap-4 mt-12"
        >
          <motion.button
            onClick={handleGoHome}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-red-400/20 border border-red-400 text-red-400 font-mono hover:bg-red-400/30 transition-all duration-300 cyber-glow"
          >
            [RETURN_TO_NEURAL_CORE] &gt;&gt;
          </motion.button>

          <motion.button
            onClick={handleGoBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-cyan-400/20 border border-cyan-400 text-cyan-400 font-mono hover:bg-cyan-400/30 transition-all duration-300"
          >
            [NAVIGATE_BACK] &lt;&lt;
          </motion.button>
        </motion.div>

        {/* System Status */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5 }}
          className="mt-12 text-center"
        >
          <div className="flex items-center justify-center space-x-4 text-xs font-mono text-gray-500">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
              <span>SYSTEM ERROR</span>
            </div>
            <div>•</div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <span>DIAGNOSTICS RUNNING</span>
            </div>
            <div>•</div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>CORE SYSTEMS ONLINE</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;